import { useEffect, useMemo, useReducer, useRef, useState } from 'react';

import isEqual from 'lodash/isEqual';

import { PHASE_FIELDS, hydratePaymentSetup } from 'lib/constants/healthcareProgram/paymentSetup';
import { useHealthcareProgramConfig } from 'lib/hooks/queries/healthcareProgram/useHealthcareProgramConfig';
import { useUpdateHealthcareProgramConfig } from 'lib/hooks/queries/healthcareProgram/useUpdateHealthcareProgramConfig';

import { hydrateAction, paymentSetupReducer } from './paymentSetupReducer';

export const usePaymentProgramDraft = ({ setIsDirty, practiceId, enabled = true } = {}) => {
  const hasHydrated = useRef(false);
  const prevPracticeIdRef = useRef(practiceId);
  if (prevPracticeIdRef.current !== practiceId) {
    prevPracticeIdRef.current = practiceId;
    hasHydrated.current = false;
  }

  const { data, isLoading, isError } = useHealthcareProgramConfig({
    ...(practiceId != null && { practiceId }),
    enabled
  });

  const persistedSetup = useMemo(
    () => hydratePaymentSetup(data?.payment_program),
    [data?.payment_program]
  );

  const [draft, dispatch] = useReducer(paymentSetupReducer, persistedSetup);
  const [activePhaseField, setActivePhaseField] = useState(PHASE_FIELDS.IN_NETWORK);

  useEffect(() => {
    if (data == null || hasHydrated.current) return;
    dispatch(hydrateAction(hydratePaymentSetup(data.payment_program)));
    hasHydrated.current = true;
  }, [data, practiceId]);

  const isDirty = useMemo(() => !isEqual(draft, persistedSetup), [draft, persistedSetup]);

  useEffect(() => {
    setIsDirty?.(isDirty);
    return () => setIsDirty?.(false);
  }, [isDirty, setIsDirty]);

  const {
    mutate: saveConfig,
    mutateAsync: saveConfigAsync,
    isLoading: isSaving
  } = useUpdateHealthcareProgramConfig({ practiceId });

  const save = () => saveConfig({ payment_program: draft });

  const saveAsync = async () => {
    const res = await saveConfigAsync({ payment_program: draft });
    return res?.code === 0;
  };

  return {
    data,
    isLoading,
    isError,
    draft,
    dispatch,
    activePhaseField,
    setActivePhaseField,
    isDirty,
    isSaving,
    save,
    saveAsync
  };
};
